import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from '../firebase/config';
import { 
  getUserProfile, 
  createUserProfile, 
  updateUserProfile,
  AuthService 
} from '../firebase/unifiedDataService';
import { USER_ROLES, hasPermission, isAdmin, isTeacherOrAdmin } from '../config/userRoles';
import { validateEmailForRole } from '../utils/domainValidator';
import { 
  ADMIN_EMAILS,
  TEACHER_EMAILS
} from '../config/emailDomains';
import { Logger } from '../utils/logger';
import { authStorage } from '../services/storageService';

const log = Logger.create('UnifiedAuth');

// Create the unified auth context
const UnifiedAuthContext = createContext(null);

// Action Code Settings for Magic Link
const getActionCodeSettings = (role) => ({
  url: `${window.location.origin}/auth/complete-login?role=${role}`,
  handleCodeInApp: true
});

/**
 * UnifiedAuthProvider - Manages authentication for all user types
 * Uses Firebase Email Link (Magic Link) authentication
 * Supports: Admin, Teacher, Student roles
 */
export function UnifiedAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initializing, setInitializing] = useState(true);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = AuthService.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        // Load user profile from database
        await loadUserProfile(firebaseUser);
        setUser(firebaseUser);
      } else {
        setUser(null);
        setUserProfile(null);
      }
      setLoading(false);
      setInitializing(false);
    });

    return () => unsubscribe();
  }, []);

  // Load user profile from Firestore ONLY (Clean architecture - no legacy fallback)
  const loadUserProfile = async (firebaseUser) => {
    try {
      // Load from Firestore - the SINGLE source of truth
      const profile = await getUserProfile(firebaseUser.uid);
      
      if (profile) {
        log.success('Loaded user profile from Firestore:', firebaseUser.uid, 'role:', profile.role);
        setUserProfile(profile);
        // Update last login
        await updateUserProfile(firebaseUser.uid, { lastLogin: Date.now() });
        return;
      }
      
      // No profile in Firestore - this shouldn't happen with new registration flow
      log.warn('No profile in Firestore for user:', firebaseUser.uid);
      log.warn('User needs to register properly or profile needs migration.');
      setUserProfile(null);
    } catch (err) {
      log.error('Error loading user profile:', err);
      setUserProfile(null);
    }
  };

  // Clear error
  const clearError = useCallback(() => setError(null), []);

  /**
   * Send Magic Link to user's email
   * @param {string} email - User's email
   * @param {string} requestedRole - Requested role (student, teacher, admin)
   * @returns {Promise<{success: boolean, message: string}>}
   */
  const sendMagicLink = async (email, requestedRole = USER_ROLES.STUDENT) => {
    setError(null);
    
    const normalizedEmail = email.toLowerCase().trim();
    
    // Validate email domain with regex pattern
    const validation = await validateEmailForRole(normalizedEmail, requestedRole);
    if (!validation.allowed) {
      const errorMsg = validation.error || 'Die E-Mail-Adresse ist nicht erlaubt.';
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    // Check if role is appropriate for this email
    // For admin: must be in whitelist
    if (requestedRole === USER_ROLES.ADMIN && !ADMIN_EMAILS.includes(normalizedEmail)) {
      const errorMsg = 'Diese E-Mail-Adresse ist nicht für Admin-Zugang autorisiert.';
      setError(errorMsg);
      throw new Error(errorMsg);
    }
    
    // For teachers: either in whitelist or existing teacher in DB
    if (requestedRole === USER_ROLES.TEACHER && !TEACHER_EMAILS.includes(normalizedEmail)) {
      // Check if already a teacher in database
      const existingTeacher = await checkExistingUserRole(normalizedEmail, USER_ROLES.TEACHER);
      if (!existingTeacher) {
        // Not in whitelist and not existing teacher - default to student
        // Unless an admin promotes them later
        requestedRole = USER_ROLES.STUDENT;
      }
    }

    try {
      await AuthService.sendMagicLink(normalizedEmail, getActionCodeSettings(requestedRole));
      
      // Store email and role for later verification
      authStorage.set('emailForSignIn', normalizedEmail);
      authStorage.set('requestedRole', requestedRole);
      
      return { 
        success: true, 
        message: 'Ein Login-Link wurde an deine E-Mail-Adresse gesendet!',
        role: requestedRole
      };
    } catch (err) {
      log.error('Error sending magic link:', err);
      
      let errorMessage = 'Der Link konnte nicht gesendet werden. Bitte versuche es erneut.';
      
      if (err.code === 'auth/invalid-email') {
        errorMessage = 'Ungültige E-Mail-Adresse.';
      } else if (err.code === 'auth/quota-exceeded') {
        errorMessage = 'Zu viele Anfragen. Bitte warte einen Moment.';
      }
      
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  /**
   * Login with email and password
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @returns {Promise<Object>} - User object
   */
  const loginWithPassword = async (email, password) => {
    setError(null);
    const normalizedEmail = email.toLowerCase().trim();
    
    try {
      const result = await AuthService.signInWithEmailPassword(normalizedEmail, password);
      
      // Load from Firestore ONLY
      let profile = await getUserProfile(result.user.uid);
      
      if (profile) {
        // Update last login
        await updateUserProfile(result.user.uid, { lastLogin: Date.now() });
        setUserProfile(profile);
      } else {
        // No profile exists - create new one in Firestore
        log.info('Creating new profile for password login user:', result.user.uid);
        const role = USER_ROLES.STUDENT;
        const newProfile = {
          uid: result.user.uid,
          email: normalizedEmail,
          displayName: result.user.displayName || normalizedEmail.split('@')[0],
          role: role,
          createdAt: Date.now(),
          lastLogin: Date.now(),
          enrolledCourses: {},
          completedQuizzes: {},
          settings: {
            theme: 'light',
            notifications: true
          }
        };
        
        await createUserProfile(result.user.uid, newProfile);
        setUserProfile(newProfile);
      }
      
      return result.user;
    } catch (err) {
      log.error('Password login error:', err);
      
      let errorMessage = 'Anmeldung fehlgeschlagen';
      
      switch (err.code) {
        case 'auth/invalid-email':
          errorMessage = 'Ungültige E-Mail-Adresse';
          break;
        case 'auth/user-disabled':
          errorMessage = 'Dieser Account wurde deaktiviert';
          break;
        case 'auth/user-not-found':
          errorMessage = 'Benutzer nicht gefunden';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Falsches Passwort';
          break;
        case 'auth/invalid-credential':
          errorMessage = 'Ungültige Anmeldedaten';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Zu viele Versuche. Bitte warte einen Moment.';
          break;
        default:
          errorMessage = err.message;
      }
      
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  /**
   * Check if email already has a user with specific role (in Firestore)
   */
  const checkExistingUserRole = async (email, role) => {
    try {
      // Query Firestore for users with this email and role
      const usersRef = collection(firestore, 'users');
      const q = query(usersRef, where('email', '==', email.toLowerCase()), where('role', '==', role));
      const snapshot = await getDocs(q);
      return !snapshot.empty;
    } catch {
      return false;
    }
  };

  /**
   * Complete Magic Link sign-in after user clicks the link
   * @returns {Promise<Object|null>} - User object or null
   */
  const completeMagicLinkSignIn = async () => {
    setError(null);
    
    // Check if the current URL is a sign-in link
    if (!AuthService.isSignInLink(window.location.href)) {
      return null;
    }

    // Get email from localStorage
    let email = authStorage.get('emailForSignIn');
    let requestedRole = authStorage.get('requestedRole') || USER_ROLES.STUDENT;
    
    // If email is not in localStorage (different device/browser)
    if (!email) {
      email = window.prompt('Bitte bestätige deine E-Mail-Adresse:');
      
      if (!email) {
        setError('E-Mail-Adresse erforderlich.');
        return null;
      }
    }

    const normalizedEmail = email.toLowerCase().trim();

    // Validate email domain with regex pattern
    const validation = await validateEmailForRole(normalizedEmail, requestedRole);
    if (!validation.allowed) {
      const errorMsg = validation.error || 'Die E-Mail-Adresse ist nicht erlaubt.';
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    try {
      const result = await AuthService.signInWithEmailLink(normalizedEmail, window.location.href);
      
      // Clear localStorage
      authStorage.remove('emailForSignIn');
      authStorage.remove('requestedRole');
      
      // Determine final role
      const finalRole = await determineFinalRole(normalizedEmail, requestedRole, result.user.uid);
      
      // Create or update user profile
      await ensureUserProfile(result.user, normalizedEmail, finalRole);
      
      return result.user;
    } catch (err) {
      log.error('Error completing magic link sign-in:', err);
      
      let errorMessage = 'Anmeldung fehlgeschlagen. Der Link ist möglicherweise abgelaufen.';
      
      if (err.code === 'auth/invalid-action-code') {
        errorMessage = 'Der Link ist ungültig oder bereits verwendet worden.';
      } else if (err.code === 'auth/expired-action-code') {
        errorMessage = 'Der Link ist abgelaufen. Bitte fordere einen neuen an.';
      }
      
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  /**
   * Determine final role for user (from email whitelists only - no legacy DB lookup)
   */
  const determineFinalRole = async (email, requestedRole, uid) => {
    const normalizedEmail = email.toLowerCase().trim();
    
    // Check if admin
    if (ADMIN_EMAILS.includes(normalizedEmail)) {
      return USER_ROLES.ADMIN;
    }
    
    // Check if teacher
    if (TEACHER_EMAILS.includes(normalizedEmail)) {
      return USER_ROLES.TEACHER;
    }
    
    // Check existing Firestore profile
    const profile = await getUserProfile(uid);
    if (profile?.role) {
      // Keep existing role if it's higher than requested
      if (profile.role === USER_ROLES.ADMIN) return USER_ROLES.ADMIN;
      if (profile.role === USER_ROLES.TEACHER && requestedRole !== USER_ROLES.ADMIN) {
        return USER_ROLES.TEACHER;
      }
    }
    
    // Default to requested role or student
    return requestedRole || USER_ROLES.STUDENT;
  };

  /**
   * Create or update user profile in Firestore ONLY (clean architecture)
   */
  const ensureUserProfile = async (firebaseUser, email, role) => {
    // Check Firestore
    let profile = await getUserProfile(firebaseUser.uid);
    
    if (profile) {
      // Update existing profile in Firestore
      const updates = {
        lastLogin: Date.now(),
        email: email.toLowerCase()
      };
      
      // Only update role if it's a promotion (not demotion)
      const existingRole = profile.role;
      if (shouldUpdateRole(existingRole, role)) {
        updates.role = role;
      }
      
      await updateUserProfile(firebaseUser.uid, updates);
      setUserProfile({ ...profile, ...updates });
    } else {
      // Create new profile in Firestore ONLY
      const displayName = email.split('@')[0];
      const newProfile = {
        uid: firebaseUser.uid,
        email: email.toLowerCase(),
        displayName: displayName,
        role: role,
        createdAt: Date.now(),
        lastLogin: Date.now(),
        enrolledCourses: {},  // For students
        completedQuizzes: {}, // For students
        settings: {
          theme: 'light',
          notifications: true
        }
      };
      
      await createUserProfile(firebaseUser.uid, newProfile);
      setUserProfile(newProfile);
      log.success('Created new user profile in Firestore:', firebaseUser.uid, 'role:', role);
    }
  };

  /**
   * Check if role should be updated (only promotions allowed)
   */
  const shouldUpdateRole = (currentRole, newRole) => {
    if (!currentRole) return true;
    if (currentRole === USER_ROLES.ADMIN) return false;
    if (currentRole === USER_ROLES.TEACHER && newRole !== USER_ROLES.ADMIN) return false;
    return true;
  };

  /**
   * Sign out
   */
  const logout = async () => {
    setError(null);
    try {
      await AuthService.signOut();
      setUser(null);
      setUserProfile(null);
    } catch (err) {
      setError('Abmeldung fehlgeschlagen');
      throw err;
    }
  };

  /**
   * Set password for user (first time)
   * @param {string} newPassword - New password to set
   * @returns {Promise<void>}
   */
  const setPassword = async (newPassword) => {
    if (!user) {
      throw new Error('Kein Benutzer angemeldet');
    }

    try {
      await AuthService.updatePassword(newPassword);
      
      // Mark password as set in profile
      await updateUserProfileData({ hasPassword: true });
      
      return { success: true };
    } catch (err) {
      log.error('Error setting password:', err);
      
      let errorMessage = 'Passwort konnte nicht gesetzt werden';
      
      if (err.code === 'auth/weak-password') {
        errorMessage = 'Passwort ist zu schwach. Mindestens 6 Zeichen erforderlich.';
      } else if (err.code === 'auth/requires-recent-login') {
        errorMessage = 'Bitte melden Sie sich erneut an, um ein Passwort zu setzen.';
      }
      
      throw new Error(errorMessage);
    }
  };

  /**
   * Change password (requires current password for reauthentication)
   * @param {string} currentPassword - Current password for verification
   * @param {string} newPassword - New password to set
   * @returns {Promise<void>}
   */
  const changePassword = async (currentPassword, newPassword) => {
    if (!user || !user.email) {
      throw new Error('Kein Benutzer angemeldet');
    }

    try {
      // Reauthenticate and update password in one call
      await AuthService.changePassword(currentPassword, newPassword);
      
      return { success: true };
    } catch (err) {
      log.error('Error changing password:', err);
      
      let errorMessage = 'Passwort konnte nicht geändert werden';
      
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        errorMessage = 'Aktuelles Passwort ist falsch';
      } else if (err.code === 'auth/weak-password') {
        errorMessage = 'Neues Passwort ist zu schwach. Mindestens 6 Zeichen erforderlich.';
      } else if (err.code === 'auth/requires-recent-login') {
        errorMessage = 'Bitte melden Sie sich erneut an';
      }
      
      throw new Error(errorMessage);
    }
  };

  /**
   * Check if user has password set
   * @returns {boolean}
   */
  const hasPassword = () => {
    return userProfile?.hasPassword === true;
  };

  /**
   * Update user profile (Context method)
   */
  const updateUserProfileData = async (updates) => {
    if (!user) throw new Error('Nicht angemeldet');
    
    try {
      // Update in Firestore
      await updateUserProfile(user.uid, updates);
      
      setUserProfile(prev => ({ ...prev, ...updates }));
    } catch (err) {
      log.error('Error updating profile:', err);
      throw err;
    }
  };

  /**
   * Admin: Promote user to teacher
   */
  const promoteToTeacher = async (targetUid) => {
    if (!userProfile || userProfile.role !== USER_ROLES.ADMIN) {
      throw new Error('Nur Admins können Benutzer befördern');
    }
    
    const updateData = { 
      role: USER_ROLES.TEACHER,
      promotedAt: Date.now(),
      promotedBy: user.uid
    };
    
    // Update Firestore ONLY
    await updateUserProfile(targetUid, updateData);
  };

  /**
   * Admin: Demote user to student
   */
  const demoteToStudent = async (targetUid) => {
    if (!userProfile || userProfile.role !== USER_ROLES.ADMIN) {
      throw new Error('Nur Admins können Benutzer zurückstufen');
    }
    
    const updateData = { 
      role: USER_ROLES.STUDENT,
      demotedAt: Date.now(),
      demotedBy: user.uid
    };
    
    // Update Firestore ONLY
    await updateUserProfile(targetUid, updateData);
  };

  /**
   * Admin: Delete user
   * Marks user as deleted in Firestore (NOT from Firebase Auth - admin must do that manually)
   */
  const deleteUser = async (targetUid) => {
    if (!userProfile || userProfile.role !== USER_ROLES.ADMIN) {
      throw new Error('Nur Admins können Benutzer löschen');
    }
    
    // Prevent deleting self
    if (targetUid === user.uid) {
      throw new Error('Du kannst dich nicht selbst löschen');
    }
    
    // Mark as deleted in Firestore ONLY
    await updateUserProfile(targetUid, { deleted: true, deletedAt: Date.now(), deletedBy: user.uid });
  };

  /**
   * Check if current user has permission
   */
  const checkPermission = useCallback((permission) => {
    if (!userProfile?.role) return false;
    return hasPermission(userProfile.role, permission);
  }, [userProfile]);

  // Context value
  const value = {
    // User state
    user,
    userProfile,
    loading,
    error,
    initializing,
    
    // Role helpers
    role: userProfile?.role || null,
    isAdmin: userProfile?.role === USER_ROLES.ADMIN,
    isTeacher: userProfile?.role === USER_ROLES.TEACHER,
    isStudent: userProfile?.role === USER_ROLES.STUDENT,
    isTeacherOrAdmin: userProfile?.role === USER_ROLES.ADMIN || userProfile?.role === USER_ROLES.TEACHER,
    isAuthenticated: !!user && !!userProfile,
    
    // Auth methods
    loginWithPassword,
    sendMagicLink,
    completeMagicLinkSignIn,
    logout,
    clearError,
    
    // Password management
    setPassword,
    changePassword,
    hasPassword,
    
    // Profile methods
    updateUserProfile: updateUserProfileData,
    
    // Admin methods
    promoteToTeacher,
    demoteToStudent,
    deleteUser,
    
    // Permission helpers
    checkPermission,
    hasPermission: (permission) => hasPermission(userProfile?.role, permission)
  };

  return (
    <UnifiedAuthContext.Provider value={value}>
      {children}
    </UnifiedAuthContext.Provider>
  );
}

/**
 * Hook to use the unified auth context
 */
export function useUnifiedAuth() {
  const context = useContext(UnifiedAuthContext);
  if (!context) {
    throw new Error('useUnifiedAuth must be used within a UnifiedAuthProvider');
  }
  return context;
}

export default UnifiedAuthContext;
